import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { PersistGate } from 'redux-persist/integration/react';
import { AppContextProvider } from './core/context';
import store, { persistor } from './store';
import { Session, SessionContextProvider, useSession } from '@supabase/auth-helpers-react';
import { supabase } from './lib/server/supabase';
import ChatPage from './components/pages/chat';
import LandingPage from './components/pages/landing';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';

if (!Cookies.get('sgpt-sesh')) {
  Cookies.set('sgpt-sesh', uuidv4(), { expires: 1 / (24 * 2), secure: true, sameSite: 'strict' });
}

const router = createBrowserRouter([
    {
        path: "/",
        element: <AppContextProvider>
            <LandingPage landing={true} />
        </AppContextProvider>,
    },
    {
        path: "/chat/:id",
        element: <AppContextProvider>
            <ChatPage />
        </AppContextProvider>,
    },
    {
        path: "/s/:id",
        element: <AppContextProvider>
            <ChatPage share={true} />
        </AppContextProvider>,
    },
    {
        path: "/s/:id/*",
        element: <AppContextProvider>
            <ChatPage share={true} />
        </AppContextProvider>,
    },
]);

const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
);

async function loadLocaleData(locale: string) {
    const response = await fetch(`/lang/${locale}.json`);
    if (!response.ok) {
        throw new Error("Failed to load locale data");
    }
    const messages: any = await response.json();
    for (const key of Object.keys(messages)) {
        if (typeof messages[key] !== 'string') {
            messages[key] = messages[key].defaultMessage;
        }
    }
    return messages;
}

async function bootstrapApplication() {
    const locale = navigator.language;

    let messages: any;
    let supportedLocale = ["en-us", "it-it"].includes(locale.toLocaleLowerCase()) ? locale.toLocaleLowerCase() : "en-us";
    try {
        messages = await loadLocaleData(supportedLocale);
    } catch (e) {
        console.warn("No locale data for", locale);
    }


    root.render(
        <React.StrictMode>
        <SessionContextProvider supabaseClient={supabase}>
            <IntlProvider locale={navigator.language} defaultLocale="en-GB" messages={messages}>
                <MantineProvider theme={{ colorScheme: "dark" }}>
                    <Provider store={store}>
                        <PersistGate loading={null} persistor={persistor}>
                            <ModalsProvider>
                                <RouterProvider router={router} />
                            </ModalsProvider>
                        </PersistGate>
                    </Provider>
                </MantineProvider>
            </IntlProvider>
        </SessionContextProvider>
        </React.StrictMode>
    );

}

bootstrapApplication();
